import React from 'react';

import LookupStatusBadge from '~components/LookupStatusBadge';

import { PaymentLookupStatus } from '~direct-debits/models';

interface ComponentProps {
  // Accept either a row carrying `status` (table-view custom cell shape) or
  // a direct status prop. Either form may be null/undefined when the API has
  // no status to report (e.g. a payment row that has not started processing).
  row?: { status: PaymentLookupStatus | null | undefined };
  status?: PaymentLookupStatus | null;
}

// Colour-override map mirrors the mandate badge palette so the same status word
// renders the same colour on every Direct Debits tab. The API stores the
// GoCardless / lookup-table originals, which are Material shades; these are
// Bootstrap 5's standard badge colours, which is what the rest of the app uses.
//
// The map covers every tag this badge can be handed - batch statuses, payment
// statuses, client-invoice statuses and refund statuses - grouped by what the
// status means rather than by which lookup table it came from, so a settled
// payment and a paid invoice read as the same green. Any tag not listed falls
// through to the stored colour.
const COLOUR_OVERRIDES: Record<string, string> = {
  // Settled successfully.
  paid: '198754',                      // bs-success
  paid_out: '198754',                  // bs-success
  funds_returned: '198754',            // bs-success
  // In flight - we have done our part and are waiting on the bank or the payer.
  // Matches the mandate badge, where `submitted` is bs-info rather than
  // bs-warning so it reads as a distinct milestone from the pending states.
  submitted: '0DCAF0',                 // bs-info
  confirmed: '0DCAF0',                 // bs-info
  sent: '0DCAF0',                      // bs-info
  // Not yet actioned.
  pending: 'FFC107',                   // bs-warning
  pending_submission: 'FFC107',        // bs-warning
  pending_customer_approval: 'FFC107', // bs-warning
  created: 'FFC107',                   // bs-warning
  draft: 'FFC107',                     // bs-warning
  unpaid: 'FFC107',                    // bs-warning
  // Money did not move, or came back.
  failed: 'DC3545',                    // bs-danger
  customer_approval_denied: 'DC3545',  // bs-danger
  charged_back: 'DC3545',              // bs-danger
  bounced: 'DC3545',                   // bs-danger
  // Closed without payment.
  cancelled: '6c757d',                 // bs-secondary
  deleted: '6c757d',                   // bs-secondary
  void: '6c757d',                      // bs-secondary
  // Unwound after payment - a separate lifecycle from "never paid", so kept
  // visually distinct from the greys above.
  refunded: '212529',                  // bs-dark
  credit_note: '212529',               // bs-dark
};

// Bootstrap 5's `--bs-secondary` — the neutral grey used for the "no status" placeholder.
// Hard-coded so the placeholder doesn't drift if the theme is recoloured (the theme is
// scoped to live data; placeholders should stay neutral).
const PLACEHOLDER_GREY = '6c757d';
const PLACEHOLDER_STATUS: PaymentLookupStatus = { tag: 'none', name: '-', colour: PLACEHOLDER_GREY };

// Thin wrapper over the shared LookupStatusBadge that adds the null-status
// placeholder behaviour expected by the payment-batches UI.
const PaymentStatusBadge: React.FC<ComponentProps> = ({ row, status }) => {
  const value = row?.status ?? status ?? null;

  if (!value) {
    return <LookupStatusBadge status={PLACEHOLDER_STATUS} dataCy="dd-payment-status-none" />;
  }

  const overrideColour = COLOUR_OVERRIDES[value.tag];
  const resolvedStatus = overrideColour ? { ...value, colour: overrideColour } : value;

  return <LookupStatusBadge status={resolvedStatus} dataCy={`dd-payment-status-${value.tag}`} />;
};

export default PaymentStatusBadge;
